import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FormField } from "@/store/types/aichat";

export interface Message {
  id: number;
  type: "user" | "assistant";
  content: string;
  searchResults?: SearchResult[];
  timestamp: string;
  ctas?: Record<string, any>;
  followUpQuestions?: string[];
}

interface SearchResult {
  id: string;
  country: string;
  college_name: string;
  course_details: {
    course_name: string;
    course_level: string;
    course_stream: string[];
    course_specialisation: string;
  };
}

interface ChatState {
  messages: Message[];
  embeddingQuery: string;
  isLoading: boolean;
  preferencesFormSubmitted: boolean;
  hasWelcomed: boolean;
  // Server-driven form state — persisted in Redux so it survives navigation
  showDynamicForm: boolean;
  dynamicFields: FormField[];
  activeFlow: string | null;
  flowStep: string | number | null;
}

const initialState: ChatState = {
  messages: [],
  isLoading: false,
  embeddingQuery: "",
  preferencesFormSubmitted: false,
  hasWelcomed: false,
  showDynamicForm: false,
  dynamicFields: [],
  activeFlow: null,
  flowStep: null,
};

const chatBotSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    addMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    clearMessages: (state) => {
      state.messages = [];
      state.preferencesFormSubmitted = false;
      state.embeddingQuery = "";
      state.showDynamicForm = false;
      state.dynamicFields = [];
      state.activeFlow = null;
      state.flowStep = null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPreferencesFormSubmitted: (state, action) => {
      state.preferencesFormSubmitted = action.payload;
    },
    setEmbeddingQuery: (state, action) => {
      state.embeddingQuery = action.payload;
    },
    setHasWelcomed: (state, action) => {
      state.hasWelcomed = action.payload;
    },
    // Server-driven form actions
    setShowDynamicForm: (state, action: PayloadAction<boolean>) => {
      state.showDynamicForm = action.payload;
    },
    setDynamicFields: (state, action: PayloadAction<FormField[]>) => {
      state.dynamicFields = action.payload;
    },
    setActiveFlow: (state, action: PayloadAction<string | null>) => {
      state.activeFlow = action.payload;
    },
    setFlowStep: (state, action: PayloadAction<string | number | null>) => {
      state.flowStep = action.payload;
    },
    updateLastMessage: (state, action: PayloadAction<Partial<Message>>) => {
      if (state.messages.length > 0) {
        const index = state.messages.length - 1;
        if (state.messages[index].type === "assistant") {
          state.messages[index] = {
            ...state.messages[index],
            ...action.payload,
          };
        }
      }
    },
    addOrUpdateAssistantMessage: (state, action: PayloadAction<string>) => {
      if (state.messages.length > 0) {
        const lastMsg = state.messages[state.messages.length - 1];
        if (lastMsg.type === "assistant") {
          lastMsg.content = action.payload;
          return;
        }
      }
      state.messages.push({
        id: Date.now(),
        type: "assistant",
        content: action.payload,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      });
    },
    addOrUpdateAssistantMessageComplete: (state, action: PayloadAction<Partial<Message>>) => {
      if (state.messages.length > 0) {
        const index = state.messages.length - 1;
        if (state.messages[index].type === "assistant") {
          state.messages[index] = {
            ...state.messages[index],
            ...action.payload,
          };
          return;
        }
      }
      state.messages.push({
        id: Date.now(),
        type: "assistant",
        content: action.payload.content || "",
        searchResults: action.payload.searchResults,
        ctas: action.payload.ctas,
        followUpQuestions: action.payload.followUpQuestions,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      });
    },
  },
});

export const {
  addMessage,
  clearMessages,
  setLoading,
  setEmbeddingQuery,
  setPreferencesFormSubmitted,
  setHasWelcomed,
  setShowDynamicForm,
  setDynamicFields,
  setActiveFlow,
  setFlowStep,
  updateLastMessage,
  addOrUpdateAssistantMessage,
  addOrUpdateAssistantMessageComplete,
} = chatBotSlice.actions;

export default chatBotSlice.reducer;
